import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  TextField,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Switch,
  FormControlLabel,
  Paper,
  MenuItem,
  makeStyles,
} from "@material-ui/core";
import { Delete, Edit, Save, Add } from "@material-ui/icons";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  links: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  modeChip: {
    fontSize: 12,
    textTransform: "none",
  },
}));

const MODES = ["atendimento", "postsale", "aftercare"];

const emptyForm = {
  id: null,
  name: "",
  prompt: "",
  mode: "atendimento",
  isActive: true,
};

const modeLabel = mode => {
  const key = `settings.settings.aiPrompts.modes.${mode || "atendimento"}`;
  const translated = i18n.t(key);
  return translated === key ? mode : translated;
};

const formatLinks = prompt => {
  const links = prompt?.links;
  if (links) {
    const parts = [];
    if (links.atendimento?.length) {
      parts.push(
        `${modeLabel("atendimento")}: ${links.atendimento
          .map(w => w.name)
          .join(", ")}`
      );
    }
    if (links.postsale?.length) {
      parts.push(
        `${modeLabel("postsale")}: ${links.postsale.map(w => w.name).join(", ")}`
      );
    }
    if (links.aftercare?.length) {
      parts.push(
        `${modeLabel("aftercare")}: ${links.aftercare
          .map(w => w.name)
          .join(", ")}`
      );
    }
    return parts.length ? parts.join(" · ") : "—";
  }
  return (prompt?.whatsapps || []).length
    ? (prompt.whatsapps || []).map(w => w?.name).filter(Boolean).join(", ")
    : "—";
};

const AiPromptsManager = () => {
  const classes = useStyles();
  const [prompts, setPrompts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      await api.post("/ai-prompts/seed-from-fallback");
    } catch (_seedErr) {
      // Seed is best-effort.
    }
    try {
      const { data } = await api.get("/ai-prompts");
      setPrompts(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => setForm({ ...emptyForm });

  const handleNameChange = e => {
    const name = e?.target?.value ?? "";
    setForm(prev => ({ ...(prev || emptyForm), name }));
  };

  const handlePromptChange = e => {
    const prompt = e?.target?.value ?? "";
    setForm(prev => ({ ...(prev || emptyForm), prompt }));
  };

  const handleModeChange = e => {
    const mode = e?.target?.value || "atendimento";
    setForm(prev => ({ ...(prev || emptyForm), mode }));
  };

  const handleActiveChange = (e, checked) => {
    const isActive =
      typeof checked === "boolean" ? checked : Boolean(e?.target?.checked);
    setForm(prev => ({ ...(prev || emptyForm), isActive }));
  };

  const handleSave = async () => {
    const name = (form?.name || "").trim();
    const promptText = form?.prompt || "";
    if (!name || !promptText.trim()) {
      toast.error(i18n.t("settings.settings.aiPrompts.required"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name,
        prompt: promptText,
        mode: MODES.includes(form?.mode) ? form.mode : "atendimento",
        isActive: form?.isActive !== false,
      };
      if (form.id) {
        await api.put(`/ai-prompts/${form.id}`, payload);
        toast.success(i18n.t("settings.settings.aiPrompts.updated"));
      } else {
        await api.post("/ai-prompts", payload);
        toast.success(i18n.t("settings.settings.aiPrompts.created"));
      }
      resetForm();
      await load();
      window.dispatchEvent(new CustomEvent("ai-prompts-changed"));
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = prompt => {
    setForm({
      id: prompt?.id ?? null,
      name: prompt?.name || "",
      prompt: prompt?.prompt || "",
      mode: MODES.includes(prompt?.mode) ? prompt.mode : "atendimento",
      isActive: prompt?.isActive !== false,
    });
  };

  const handleDelete = async id => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(i18n.t("settings.settings.aiPrompts.confirmDelete"))) {
      return;
    }
    try {
      await api.delete(`/ai-prompts/${id}`);
      toast.success(i18n.t("settings.settings.aiPrompts.deleted"));
      if (form.id === id) resetForm();
      await load();
      window.dispatchEvent(new CustomEvent("ai-prompts-changed"));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Paper className={classes.paper} variant="outlined">
      <Typography variant="h6">
        {i18n.t("settings.settings.aiPrompts.title")}
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        {i18n.t("settings.settings.aiPrompts.help")}
      </Typography>

      <div className={classes.form}>
        <TextField
          label={i18n.t("settings.settings.aiPrompts.name")}
          variant="outlined"
          margin="dense"
          fullWidth
          value={form?.name ?? ""}
          onChange={handleNameChange}
        />
        <TextField
          select
          label={i18n.t("settings.settings.aiPrompts.mode")}
          variant="outlined"
          margin="dense"
          fullWidth
          value={form?.mode || "atendimento"}
          onChange={handleModeChange}
          helperText={i18n.t("settings.settings.aiPrompts.modeHelp")}
        >
          {MODES.map(m => (
            <MenuItem key={m} value={m}>
              {modeLabel(m)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={i18n.t("settings.settings.aiPrompts.prompt")}
          variant="outlined"
          margin="dense"
          fullWidth
          multiline
          rows={6}
          value={form?.prompt ?? ""}
          onChange={handlePromptChange}
        />
        <FormControlLabel
          control={
            <Switch
              checked={form?.isActive !== false}
              onChange={handleActiveChange}
              color="primary"
            />
          }
          label={i18n.t("settings.settings.aiPrompts.active")}
        />
        <div className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            startIcon={form?.id ? <Save /> : <Add />}
            onClick={handleSave}
            disabled={saving}
          >
            {form?.id
              ? i18n.t("settings.settings.aiPrompts.save")
              : i18n.t("settings.settings.aiPrompts.add")}
          </Button>
          {form?.id ? (
            <Button variant="outlined" onClick={resetForm}>
              {i18n.t("settings.settings.aiPrompts.cancelEdit")}
            </Button>
          ) : null}
        </div>
      </div>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{i18n.t("settings.settings.aiPrompts.name")}</TableCell>
            <TableCell>{i18n.t("settings.settings.aiPrompts.mode")}</TableCell>
            <TableCell>
              {i18n.t("settings.settings.aiPrompts.connections")}
            </TableCell>
            <TableCell align="center">
              {i18n.t("settings.settings.aiPrompts.active")}
            </TableCell>
            <TableCell align="right">
              {i18n.t("settings.settings.aiPrompts.actions")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(Array.isArray(prompts) ? prompts : []).map(p => (
            <TableRow key={p.id}>
              <TableCell>{p?.name || ""}</TableCell>
              <TableCell className={classes.modeChip}>
                {modeLabel(p?.mode)}
              </TableCell>
              <TableCell className={classes.links}>{formatLinks(p)}</TableCell>
              <TableCell align="center">
                {p?.isActive !== false
                  ? i18n.t("settings.settings.aiPrompts.yes")
                  : i18n.t("settings.settings.aiPrompts.no")}
              </TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => handleEdit(p)}>
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(p.id)}>
                  <Delete fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {!prompts.length ? (
            <TableRow>
              <TableCell colSpan={5}>
                {i18n.t("settings.settings.aiPrompts.empty")}
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default AiPromptsManager;
